import React, { useEffect, useState, useRef } from "react";
import { Table, Tabs, Button, Card, Tag, Typography, Space, Avatar, Modal, Dropdown, Menu, message, Tooltip } from "antd";
import { PullToRefresh } from "./PullToRefresh";
import { getPendingRecords, AttendanceRecord } from "../helpers/attendanceQueueStorage";
import { userStorage, EmployeeUser } from "../helpers/userStorage";
import { useNavigate } from "react-router-dom";
import { getDecryptedName, decryptByText } from "../helpers/bycrypt";
import { getFullAwsUrl } from "../helpers/utils";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { svg } from "../Assets/svg";
import { useDispatch } from "react-redux";
import { SyncEmployeeDataAPI } from "../Admin/Redux/employeeAttendance";
import { formatTimeWithBusinessTZ, formatDateWithBusinessTZ } from "../helpers/moment";
import { useSocket } from "../contexts/SocketContext";
import { ApiOutlined, DisconnectOutlined } from "@ant-design/icons";
import { PAGE_LIMIT } from "../helpers/useEmployeeSync";
import { Network } from "@capacitor/network";
import { Capacitor } from "@capacitor/core";


dayjs.extend(duration);

const { Title } = Typography;

export const LocalDebug: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [queue, setQueue] = useState<AttendanceRecord[]>([]);
  const [users, setUsers] = useState<EmployeeUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [businessSettings, setBusinessSettings] = useState<any>({});
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const { socket, isConnected } = useSocket();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedUserIdRef = useRef<string | null>(null);
  const [isLogModalVisible, setIsLogModalVisible] = useState(false);
  const [logModalData, setLogModalData] = useState<any[]>([]);

  const updateModalDataForUser = (userId: string, userList: any[]) => {
    const currentSelectedUser = userList.find((u: any) => u._id === userId);
    if (currentSelectedUser) {
      try {
        let parsedArray = [];
        if (Array.isArray(currentSelectedUser.employeeAttendance)) {
          parsedArray = currentSelectedUser.employeeAttendance;
        } else if (typeof currentSelectedUser.employeeAttendance === "string") {
          parsedArray = JSON.parse(currentSelectedUser.employeeAttendance);
        }
        let serverAttendances = parsedArray.map((r: any) => {
          if (typeof r === "string") {
            try { return JSON.parse(decryptByText(r)); } catch (e) { return r; }
          }
          return r;
        });
        serverAttendances.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setLogModalData(serverAttendances);
      } catch (e) { }
    }
  };

  const handleSilentSync = async () => {
    if (!isConnected || !socket?.connected) {
      console.log("LocalDebug: Socket is offline, skipping handleSilentSync");
      return;
    }
    console.log("LocalDebug: Received socket event, executing handleSilentSync...");
    console.log("LocalDebug: Syncing latest data via socket...");

    const lastSyncDate = await userStorage.getSyncMetadata();
    (dispatch as any)(
      SyncEmployeeDataAPI({
        filters: { page: 1, limit: PAGE_LIMIT, last_sync_date: lastSyncDate || undefined },
        onSuccess: async (res: any) => {
          const fetchedUsers = res?.data || [];
          const deletedIds = res?.deleted_ids || [];
          if (fetchedUsers.length > 0) {
            await userStorage.bulkSave(fetchedUsers);
          }
          if (deletedIds.length > 0) {
            await userStorage.deleteUsersByIds(deletedIds);
          }
          if (res?.business_settings && Object.keys(res.business_settings).length > 0) {
            await userStorage.saveBusinessSettings(res.business_settings);
            setBusinessSettings(res.business_settings);
          }
          if (res?.server_time) {
            await userStorage.saveSyncMetadata(res.server_time);
            setLastSyncDate(res.server_time);
          }
          const allUsers = await userStorage.getAllUsers();
          setUsers(allUsers);
          if (selectedUserIdRef.current) {
            updateModalDataForUser(selectedUserIdRef.current, allUsers);
          }
          const pending = await getPendingRecords();
          setQueue(pending);
          message.destroy('socketSync');
        },
        onError: () => {
          message.destroy('socketSync');
        }
      })
    );
  };

  useEffect(() => {
    if (socket) {
      console.log("LocalDebug: Registering socket listeners...");
      socket.on("employee-attendance-updated", handleSilentSync);
      socket.on("hr:settings-updated", handleSilentSync);
      socket.on("employee-auto-sync", handleSilentSync);
      return () => {
        console.log("LocalDebug: Removing socket listeners...");
        socket.off("employee-attendance-updated", handleSilentSync);
        socket.off("hr:settings-updated", handleSilentSync);
        socket.off("employee-auto-sync", handleSilentSync);
      };
    } else {
      console.log("LocalDebug: Socket is not connected or available!");
    }
  }, [socket]);

  const handleSyncNow = () => {
    if (!isConnected || !socket?.connected) {
      // message.warning({ content: "Cannot sync: Server is offline (socket disconnected)", key: "syncData" });
      return;
    }
    setLoading(true);
    // message.loading({ content: 'Syncing employees...', key: 'syncData' });
    console.log("calling Syncing employees");
    (dispatch as any)(
      SyncEmployeeDataAPI({
        filters: { page: 1, limit: PAGE_LIMIT },
        onSuccess: async (res: any) => {
          await userStorage.clear(); // Clear old data for a fresh full sync
          const fetchedUsers = res?.data || [];
          if (fetchedUsers.length > 0) {
            await userStorage.bulkSave(fetchedUsers);
          }
          if (res?.business_settings && Object.keys(res.business_settings).length > 0) {
            await userStorage.saveBusinessSettings(res.business_settings);
          }
          if (res?.server_time) {
            await userStorage.saveSyncMetadata(res.server_time);
            setLastSyncDate(res.server_time);
          }
          // message.success({ content: 'Employees synced successfully!', key: 'syncData' });
          console.log("Employees synced successfully");
          const allUsers = await userStorage.getAllUsers();
          setUsers(allUsers);
          setLoading(false);
        },
        onError: (err: any) => {
          // message.error({ content: 'Sync failed', key: 'syncData' });
          // message.error({ content: 'Sync failed', key: 'syncData' });
          console.log("Sync failed");
          setLoading(false);
        }
      })
    );
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const pending = await getPendingRecords();
      setQueue(pending);

      const allUsers = await userStorage.getAllUsers();
      setUsers(allUsers);
      if (selectedUserIdRef.current) {
        updateModalDataForUser(selectedUserIdRef.current, allUsers);
      }

      const bSettings = await userStorage.getBusinessSettings();
      setBusinessSettings(bSettings || {});

      const syncDate = await userStorage.getSyncMetadata();
      setLastSyncDate(syncDate);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    const handleSyncCompleted = () => {
      console.log("LocalDebug: Background sync completed. Refreshing data...");
      fetchData();
    };

    window.addEventListener("employee-sync-completed", handleSyncCompleted);
    window.addEventListener("attendance-queue-updated", handleSyncCompleted);

    let networkListener: any;
    if (Capacitor.isNativePlatform()) {
      Network.addListener("networkStatusChange", (status) => {
        console.log("LocalDebug: Network status changed:", status.connected ? "Online" : "Offline", status);
        if (status.connected) {
          console.log("LocalDebug: Network reconnected, fetching latest data...");
          fetchData();
          if (isConnected) {
            handleSyncNow();
          }
        }
      }).then((listener) => {
        networkListener = listener;
      });
    }

    return () => {
      window.removeEventListener("employee-sync-completed", handleSyncCompleted);
      window.removeEventListener("attendance-queue-updated", handleSyncCompleted);
      if (networkListener) {
        networkListener.remove();
      }
    };
  }, []);

  const handleRowClick = (record: any) => {
    try {
      selectedUserIdRef.current = record._id;
      setSelectedUserId(record._id);
      let parsedArray = [];
      if (Array.isArray(record.employeeAttendance)) {
        parsedArray = record.employeeAttendance;
      } else if (typeof record.employeeAttendance === "string") {
        parsedArray = JSON.parse(record.employeeAttendance);
      }
      let serverAttendances = parsedArray.map((r: any) => {
        if (typeof r === "string") {
          try { return JSON.parse(decryptByText(r)); } catch (e) { return r; }
        }
        return r;
      });
      // Sort to show latest at top or keep sequential
      serverAttendances.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setLogModalData(serverAttendances);
      setIsLogModalVisible(true);
    } catch (e) { }
  };

  const logColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "date",
      render: (val: any) => val ? formatDateWithBusinessTZ(val, "DD-MM-YYYY") : "-",
    },
    {
      title: "Clock In",
      dataIndex: "clockIn",
      key: "clockIn",
      render: (_: any, record: any) => record.clockIn?.start ? formatTimeWithBusinessTZ(record.clockIn.start, "hh:mm A") : (record.created_at ? formatTimeWithBusinessTZ(record.created_at, "hh:mm A") : "---"),
    },
    {
      title: "Clock Out",
      dataIndex: "clockOut",
      key: "clockOut",
      render: (_: any, record: any) => {
        if (!record.clockOut) return "---";
        return record.clockOut?.end ? formatTimeWithBusinessTZ(record.clockOut.end, "hh:mm A") : (record.updated_at ? formatTimeWithBusinessTZ(record.updated_at, "hh:mm A") : "---");
      }
    },
    {
      title: "Clocked Hours",
      key: "timeLog",
      render: (_: any, record: any) => {
        const start = record.clockIn?.start || record.created_at;
        const end = record.clockOut?.end || record.updated_at;
        if (start && end && record.clockOut) {
          const diff = dayjs(end).diff(dayjs(start));
          return dayjs.duration(diff).format("HH:mm:ss");
        }
        return "---";
      }
    }
  ];

  const queueColumns = [
    { title: "ID", dataIndex: "id", key: "id", ellipsis: true },
    { title: "User ID", dataIndex: "user", key: "user" },
    { title: "Name", key: "name", render: (text: any, record: any) => getDecryptedName(record) },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => <Tag color={type === "clock-in" ? "green" : "red"}>{type}</Tag>
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (ts: string) => new Date(ts).toLocaleString()
    },
    {
      title: "Photo",
      key: "photo",
      render: (text: any, record: any) => (
        record.photoBase64 ? <img src={record.photoBase64} alt="face" style={{ width: 40, height: 40, borderRadius: 20 }} /> : "N/A"
      )
    },
    { title: "AWS Key", dataIndex: "photoKey", key: "photoKey" },
  ];

  const userColumns = [
    {
      title: "Profile",
      key: "profile",
      render: (text: any, record: any) => {
        let photo = record.profile_pic || record.face_photo;
        if (!photo && record.biometric_authentication) {
          try {
            const bio = typeof record.biometric_authentication === 'string' ? JSON.parse(record.biometric_authentication) : record.biometric_authentication;
            photo = bio?.face?.photo;
          } catch (e) { }
        }
        if (!photo && record.employee_details) {
          try {
            const details = typeof record.employee_details === 'string' ? JSON.parse(record.employee_details) : record.employee_details;
            photo = details?.biometric_authentication?.face?.photo;
          } catch (e) { }
        }
        const name = getDecryptedName(record) || "Employee";
        const initials = name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
        return (
          <Space>
            {photo ? (
              <Avatar
                shape="square"
                src={photo.startsWith('data:') ? photo : getFullAwsUrl(photo)}
                size={48}
                className="avatar-flex shadow-sm"
                style={{ borderRadius: "8px" }}
              />
            ) : (
              <Avatar shape="square" size={48} className="avatar-initials shadow-sm" style={{ borderRadius: "8px", backgroundColor: "var(--bs-primary)", color: "white" }}>
                {initials}
              </Avatar>
            )}
            <span className="ms-2 fw-medium">{name}</span>
          </Space>
        );
      }
    },
    {
      title: "Attendance Status",
      key: "attendance_status",
      render: (text: any, record: any) => {
        let isClockedIn = false;
        let count = 0;
        let debugStr = "";
        try {
          let parsedArray = [];
          if (Array.isArray(record.employeeAttendance)) {
            parsedArray = record.employeeAttendance;
          } else if (typeof record.employeeAttendance === "string") {
            parsedArray = JSON.parse(record.employeeAttendance);
          }
          let serverAttendances = parsedArray.map((r: any) => {
            if (typeof r === "string") {
              try { return JSON.parse(decryptByText(r)); } catch (e) { return r; }
            }
            return r;
          });
          count = serverAttendances.length;
          serverAttendances.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          if (serverAttendances && serverAttendances.length > 0) {
            const lastServerRecord = serverAttendances[serverAttendances.length - 1];
            debugStr = lastServerRecord.clockOut ? "Has ClockOut" : "No ClockOut";
            if (lastServerRecord && !lastServerRecord.clockOut) {
              isClockedIn = true;
            }
          }
        } catch (e) { }

        return (
          <Space>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: isClockedIn ? '#52c41a' : '#f5222d' }} />
            </div>
            <span>{isClockedIn ? 'Clocked In' : 'Clocked Out'}</span>
            <span style={{ fontSize: '10px', color: '#999' }}>({count} records, {debugStr})</span>
          </Space>
        );
      }
    },
    {
      title: "Department",
      key: "department",
      render: (text: any, record: any) => {
        try {
          let dept = record.department;
          if (typeof dept === 'string') {
            try { dept = JSON.parse(dept); } catch (e) { }
          }
          if (Array.isArray(dept) && dept.length > 0) {
            return dept.map((d: any) => d.name || d.department_name).filter(Boolean).join(", ");
          }
          if (record.department_name) return record.department_name;
        } catch (e) { }
        return "N/A";
      }
    },
    {
      title: "Has Signature?",
      key: "signature",
      render: (text: any, record: any) => (
        <Tag color={record.face_signature && record.face_signature.length > 10 ? "blue" : "default"}>
          {record.face_signature && record.face_signature.length > 10 ? "Yes" : "No"}
        </Tag>
      )
    },
    {
      title: "PPE Kit Required?",
      key: "isPPEKitRequired",
      render: (text: any, record: any) => (
        <Tag color={record.isPPEKitRequired ? "purple" : "default"}>
          {record.isPPEKitRequired ? "Yes" : "No"}
        </Tag>
      )
    }
  ];

  const onRefresh = () => {
    fetchData();
    handleSyncNow();
  }

  useEffect(() => {
    document.body.classList.add('face-scanner-local-debug');
    return () => {
      document.body.classList.remove('face-scanner-local-debug');
    };
  }, []);

  return (
    <PullToRefresh onRefresh={onRefresh} className="local-debug-ptr">
      <Card style={{ margin: 0 }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="text-black fs-36 fw-bolder page-title mb-0 d-flex align-items-center gap-3">
            <span className="cursor-pointer svg-24" onClick={() => navigate(-1)}>
              {svg?.backArrow || "←"}
            </span>
            <Title level={4} style={{ margin: 0 }}>Local Storage Debug Panel</Title>
            <Tooltip title={isConnected ? "Socket Connected" : "Socket Disconnected"} trigger="click">
              {isConnected ? (
                <ApiOutlined style={{ color: '#52c41a', fontSize: '24px', cursor: 'pointer' }} />
              ) : (
                <DisconnectOutlined style={{ color: '#f5222d', fontSize: '24px', cursor: 'pointer' }} />
              )}
            </Tooltip>
          </h2>
          <div className="d-flex align-items-center gap-2">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="refreshData" onClick={fetchData}>
                    <div className="text-black d-flex align-items-center gap-2">
                      Refresh Data
                    </div>
                  </Menu.Item>
                  <Menu.Item key="syncNow" onClick={handleSyncNow}>
                    <div className="text-black d-flex align-items-center gap-2">
                      Sync Now
                    </div>
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
              placement="bottomRight"
            >
              <span className="cursor-pointer svg-46">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          </div>
        </div>
        <Tabs defaultActiveKey="0">
          <Tabs.TabPane tab="Business Settings" key="0">
            <Card bordered={false} className="mb-4 shadow-sm" bodyStyle={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>PPE Confirmation Required For</Typography.Text>
                  <Typography.Text strong>{businessSettings?.ppe_confirmation_roles || "-"}</Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Face Accuracy</Typography.Text>
                  <Typography.Text strong>{businessSettings?.faceAccuracyThreshold ? `${businessSettings.faceAccuracyThreshold}%` : "-"}</Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Time Zone</Typography.Text>
                  <Typography.Text strong>{businessSettings?.timezone || "-"}</Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Last Sync Date</Typography.Text>
                  <Typography.Text strong>
                    {lastSyncDate ? formatDateWithBusinessTZ(lastSyncDate, "DD-MM-YYYY, hh:mm A") : "-"}
                  </Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Auto Sync Multiple Device</Typography.Text>
                  <Typography.Text strong>{businessSettings?.isAutoSyncMultipleDeviceEnabled ? "On" : "Off"}</Typography.Text>
                </div>

                {businessSettings?.isAutoSyncMultipleDeviceEnabled &&
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Clock In/Out Sync Duration</Typography.Text>
                    <Typography.Text strong>{businessSettings?.autoSyncMultipleDeviceDuration ? `${businessSettings.autoSyncMultipleDeviceDuration || 0} Minutes` : "-"}</Typography.Text>
                  </div>
                }

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Auto Start/Stop – Kill App</Typography.Text>
                  <Typography.Text strong>{businessSettings?.attendance_app_schedule?.isAutoStartStopEnabled ? "On" : "Off"}</Typography.Text>
                </div>

                <div>
                  <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Auto Start / Close Time</Typography.Text>
                  <Typography.Text strong>
                    {(() => {
                      const start = businessSettings?.attendance_app_schedule?.autoStartTime ?? "08:00";
                      const close = businessSettings?.attendance_app_schedule?.autoCloseTime ?? "18:00";
                      const formattedStart = dayjs(start, "HH:mm").format("hh:mm A");
                      const formattedClose = dayjs(close, "HH:mm").format("hh:mm A");
                      return `${formattedStart} - ${formattedClose}`;
                    })()}
                  </Typography.Text>
                </div>
              </div>
            </Card>
          </Tabs.TabPane>
          <Tabs.TabPane tab={`Offline Attendance Queue (${queue.length})`} key="1">
            <Table
              dataSource={queue}
              columns={queueColumns}
              rowKey="id"
              loading={loading}
              scroll={{ x: 800 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={`Synced Employees (${users.length})`} key="2">
            <Table
              dataSource={users}
              columns={userColumns}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 800 }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' }
              })}
            />
          </Tabs.TabPane>
        </Tabs>

        <Modal
          open={isLogModalVisible}
          onCancel={() => {
            setIsLogModalVisible(false);
            selectedUserIdRef.current = null;
            setSelectedUserId(null);
          }}
          footer={null}
          width={700}
          closeIcon={svg?.closeIcon}
          centered
          destroyOnClose
          className="commonModalClass createClientDistributors"
        >
          <div className="modal-content border-0 p-0 pt-0 page-form">
            <div className="modal-header border-0 pb-1 mb-2">
              <h1 className="modal-title fs-4 fw-bolder mx-auto text-dark">
                Log History
              </h1>
            </div>
            <div className="modal-body pb-3">
              <Table
                columns={logColumns}
                dataSource={logModalData}
                pagination={false}
                rowKey={(record: any, index: any) => index}
                bordered
              />
            </div>
          </div>
        </Modal>
      </Card>
    </PullToRefresh>
  );
};

